import api from '@disco/api/server'
import { browserInstanceId } from '@disco/api/server/base'
import {
  CHANNEL_SHARE_NOTIFICATION_RECEIVE,
  CHANNEL_SHARE_NOTIFICATION_ACCEPTED,
  CLIENT_LIBRARY_NOTIFICATION_RECEIVE,
  IMPORTANT_UPDATE_AVAILABLE,
  LEAK_TRACING_NOTIFICATION_RECEIVE,
  NOTIFICATION_PANE_DISMISS,
  NOTIFICATION_PANE_EXPAND,
  NOTIFICATION_PANE_TOGGLE,
  PLAYLIST_DELETE_BATCH_SUCCESS,
  PLAYLIST_NOTIFICATION_RECEIVE,
  PLAYLIST_NOTIFICATION_RESET,
  PLAYLIST_NOTIFICATIONS_BATCH_LOAD,
  PLAYLIST_VERSION_REPLACE_SUCCESS,
  SIDEBAR_OPEN,
  TRACK_DELETE_BATCH_SUCCESS,
  BRIEF_SUBMISSION_NOTIFICATION_RECEIVE,
  MENTION_NOTIFICATION_RECEIVE,
  REACTION_NOTIFICATION_RECEIVE,
} from '@disco/constants/ActionTypes'
import { __REACT_NATIVE__ } from '@disco/constants/environment'
import { A_CAPPELLA } from '@disco/constants/Nesting'
import {
  NOTES_V2_MENTIONS,
  SESSION_BOUND_URLS,
} from '@disco/constants/FeatureTypes'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getExplorerPlaylistIds,
  getExplorerTrackIds,
} from '@disco/state-management/selectors/explorer'
import { getPlaylist } from '@disco/state-management/selectors/playlists'
import {
  getShortlists,
  getShortlistTrackIds,
} from '@disco/state-management/selectors/shortlists'
import { getTrack } from '@disco/state-management/selectors/tracks'
import { getConstanceSetting } from '@disco/state-management/selectors/user'
import captureError from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'
import { getUserDisplayName } from '@disco/utils/string-utils'
import { processUnknownNotification } from '@disco/utils/websockets'
import intersection from 'lodash/intersection'
import NavigationService from '@disco/state-management/NavigationService'
import type { Location } from 'react-router-dom'
import type {
  DismissibleNotification,
  JsonNotification,
  PongMessage,
} from 'types/notification'
import type { GetState } from 'types/redux'
// import type { ShortlistNotification } from 'types/notification'
import type { Shortlist } from 'types/shortlist'
import type { BusinessUser, UserId } from 'types/user'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import {
  clearStemSeparationPendingTimeout,
  openTrackRoute,
} from '@disco/state-management/actions/ExplorerActions'
import { applyPartialPlaylistUpdate } from '@disco/state-management/actions/playlist-actions'
import { loadBatch } from '@disco/state-management/actions/PlaylistTrackActions'
import { handleLyricsComplete } from '@disco/state-management/actions/LyricActions'
import {
  initShortlists,
  processExternalUploadFinish,
  removeTempItem,
  updateTempItemProgress,
} from '@disco/state-management/actions/ShortlistActions'
import { loadBusinessUsers } from '@disco/state-management/actions/BusinessUsersActions'
import {
  loadBusinessData,
  updateBusinessPrivateMetadata,
  updateUserBusiness,
} from '@disco/state-management/actions/UserActions'
// import similaritySearchResultsAggregatedTracks from '@disco/fixtures/similarity-search-results-aggregated-tracks'
/**
 * Loads a batch of notifications into the store.
 * @param notifications - Optional array of notifications to load
 * @returns Action object with notifications data
 */
export function loadNotifications(notifications?: JsonNotification[]): {
  notifications: void | Array<JsonNotification>
  type: string
} {
  return {
    type: PLAYLIST_NOTIFICATIONS_BATCH_LOAD,
    notifications,
  }
}
/**
 * Receives and processes a playlist-related notification.
 * @param notification - The playlist notification to process
 * @returns Action object with notification data
 */
export function receivePlaylistNotification(notification: JsonNotification): {
  notification: JsonNotification
  type: string
} {
  return {
    type: PLAYLIST_NOTIFICATION_RECEIVE,
    notification,
  }
}
/**
 * Receives and processes a brief submission notification.
 * @param notification - The brief submission notification to process
 * @returns Action object with notification data
 */
export function receiveBriefSubmissionNotification(
  notification: JsonNotification,
): {
  notification: JsonNotification
  type: string
} {
  return {
    type: BRIEF_SUBMISSION_NOTIFICATION_RECEIVE,
    notification,
  }
}

/**
 * Receives and processes a mention notification.
 * @param notification - The mention notification to process
 * @returns Action object with notification data
 */
export function receiveMentionNotification(notification: JsonNotification): {
  notification: JsonNotification
  type: string
} {
  return {
    type: MENTION_NOTIFICATION_RECEIVE,
    notification,
  }
}

export function receiveReactionNotification(notification: JsonNotification): {
  notification: JsonNotification
  type: string
} {
  return {
    type: REACTION_NOTIFICATION_RECEIVE,
    notification,
  }
}

/**
 * Receives and processes a channel share notification.
 * @param notification - The channel share notification to process
 * @returns Action object with notification data
 */
export function receiveChannelShareNotification(
  notification: JsonNotification,
): {
  notification: JsonNotification
  type: string
} {
  return {
    type: CHANNEL_SHARE_NOTIFICATION_RECEIVE,
    notification,
  }
}
/**
 * Processes an accepted channel share notification.
 * @param notification - The accepted channel share notification
 * @returns Action object with notification data
 */
export function acceptedChannelShareNotification(
  notification: JsonNotification,
): {
  notification: JsonNotification
  type: string
} {
  return {
    type: CHANNEL_SHARE_NOTIFICATION_ACCEPTED,
    notification,
  }
}
/**
 * Receives and processes a client library notification.
 * @param notification - The library notification to process
 * @returns Action object with notification data
 */
export function receiveLibraryNotification( // TODO: update flow types in mobile directory and remove | Object here
  notification: JsonNotification | Record<string, any>,
): {
  notification: any | JsonNotification
  type: string
} {
  return {
    type: CLIENT_LIBRARY_NOTIFICATION_RECEIVE,
    notification,
  }
}
/**
 * Receives and processes a leak tracing notification.
 * @param notification - The leak tracing notification to process
 * @returns Action object with notification data
 */
export function receiveLeakTracingNotification(
  notification: JsonNotification,
): {
  notification: JsonNotification
  type: string
} {
  return {
    type: LEAK_TRACING_NOTIFICATION_RECEIVE,
    notification,
  }
}
/**
 * Receives and processes an audio separation (instrumental/a cappella) creation notification.
 * Fetches the created track and original track, then shows success notification.
 *
 * The backend sends `instant_instrumental_created` for both instrumental and a cappella.
 * We determine which type was created by checking `nesting_label`, preferring the
 * notification payload and falling back to the fetched created track.
 *
 * @param notification - The audio separation creation notification
 * @returns Thunk that processes the notification and shows UI notification
 */
export function receiveAudioSeparationNotification(
  notification: JsonNotification,
): (dispatch: any) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any) => {
    if (notification.type !== 'instant_instrumental_created') return

    try {
      const [createdTrack, requestedTrack] = await Promise.all([
        api.getTrack(notification.track_id),
        api.getTrack(notification.requested_track_id),
      ])

      // Determine which type was created using nesting_label from the notification
      // payload, falling back to the created track when older notifications do not
      // include the field yet. Use case-insensitive comparison to guard against
      // casing mismatches.
      const nestingLabel =
        notification.nesting_label ?? createdTrack.nesting_label
      if (!nestingLabel) {
        captureError(
          new Error(
            'nesting_label missing from notification and created track.',
          ),
          {
            moduleName: 'receiveAudioSeparationNotification',
            extra: {
              track_id: notification.track_id,
              requested_track_id: notification.requested_track_id,
            },
          },
        )
      }

      const isACappella =
        nestingLabel?.toLowerCase() === A_CAPPELLA.toLowerCase()
      const pendingUpdate = nestingLabel
        ? isACappella
          ? { instant_a_cappella_pending: false }
          : { instant_instrumental_pending: false }
        : null

      clearStemSeparationPendingTimeout(notification.requested_track_id)

      dispatch(
        loadBatch([
          createdTrack,
          pendingUpdate
            ? { ...requestedTrack, ...pendingUpdate }
            : requestedTrack,
        ]),
      )

      const trackType = nestingLabel ?? 'Audio separation'
      const notificationUidPrefix = !nestingLabel
        ? 'audio-separation'
        : isACappella
        ? 'instant-a-cappella'
        : 'instant-instrumental'

      pushUINotification({
        ...(pendingUpdate && {
          _replaceNotificationWithUID: `${notificationUidPrefix}-pending-${notification.requested_track_id}`,
        }),
        uid: `${notificationUidPrefix}-result-${notification.track_id}`,
        level: 'success',
        title: `${trackType} ready!`,
        message: `Your ${trackType.toLowerCase()} for '${
          requestedTrack.name
        }' is ready.`,
        action: {
          label: 'Go to track',
          callback: () => dispatch(openTrackRoute(notification.track_id)),
        },
        dismissible: 'button',
      })
    } catch (error) {
      dispatch(sendGenericError(error))
    }
  }
}

/**
 * Handles remote playlist deletion by redirecting user if needed and dispatching delete action.
 * @param dispatch - Redux dispatch function
 * @param deletedPlaylistIds - Array of playlist IDs that were deleted
 * @param pathname - Current URL pathname for redirect logic
 */
const remotePlaylistDeleteAction = (dispatch, deletedPlaylistIds, pathname) => {
  const redirectToRoot = deletedPlaylistIds.some(id => {
    const match = pathname.match(`/playlists/${id}/`)
    return match && match.length > 0
  })
  if (redirectToRoot) NavigationService.push('/')
  dispatch({
    type: PLAYLIST_DELETE_BATCH_SUCCESS,
    playlistIds: deletedPlaylistIds,
  })
}

/**
 * Handles remote track deletion by redirecting user if needed and dispatching delete action.
 * @param dispatch - Redux dispatch function
 * @param deletedTrackIds - Array of track IDs that were deleted
 * @param pathname - Current URL pathname for redirect logic
 */
const remoteTrackDeleteAction = (dispatch, deletedTrackIds, pathname) => {
  const redirectToRoot = deletedTrackIds.some(id => {
    const match = pathname.match(`/tracks/${id}/`)
    return match && match.length > 0
  })
  if (redirectToRoot) NavigationService.push('/')
  dispatch({
    type: TRACK_DELETE_BATCH_SUCCESS,
    trackIds: deletedTrackIds,
  })
}

/**
 * Extracts all deleted playlist IDs from a notification, including those from deleted versions.
 * Combines deleted_playlist_ids with playlists from deleted_playlistversions where replacement is null.
 * @param notification - The deletion notification containing deleted items
 * @returns Array of all deleted playlist IDs
 */
const getAllDeletedPlaylists = (
  notification: Record<string, any>,
): number[] => {
  const results: any[] = []
  const { deleted_playlist_ids, deleted_playlistversions } = notification
  if (deleted_playlist_ids && deleted_playlist_ids.length > 0)
    results.push(...deleted_playlist_ids)

  if (deleted_playlistversions && deleted_playlistversions.length > 0) {
    const additionalResults = deleted_playlistversions
      .filter(p => !p.replacement_playlistversion_id)
      .map(p => p.playlist_id)
    results.push(...additionalResults)
  }

  return results
}

/**
 * Extracts playlists that were replaced (not deleted) from deleted playlist versions.
 * @param params - Object containing deleted playlist versions
 * @param params.deleted_playlistversions - Array of deleted playlist version objects
 * @returns Array of replaced playlist objects
 */
const getReplacedPlaylists = ({
  deleted_playlistversions,
}: Record<string, any>): Record<string, any>[] => {
  let replacedPlaylists = []

  if (deleted_playlistversions && deleted_playlistversions.length > 0) {
    replacedPlaylists = deleted_playlistversions.filter(
      p => p.replacement_playlistversion_id,
    )
  }

  return replacedPlaylists
}

/**
 * Shows a UI notification for remotely deleted items that are currently visible.
 * @param deletedIds - Array of IDs that were deleted
 * @param visibleIds - Array of IDs currently visible to the user
 * @param nameExtractor - Function to extract display name from an ID
 * @param messageTemplate - Template string for the notification message
 */
const showRemoteNotification = (
  deletedIds: number[],
  visibleIds: number[],
  nameExtractor: (id: number) => string | null | undefined,
  messageTemplate: string,
): void => {
  const notifificationItems = intersection(deletedIds, visibleIds)
  const names: string[] = notifificationItems
    .map(id => nameExtractor(id))
    .filter(id => id !== null && id !== undefined)
  if (!names.length) return
  pushUINotification({
    message: messageTemplate + names.join(', '),
    level: 'error',
  })
}

/**
 * Creates and shows notifications for remotely deleted tracks and playlists.
 * @param user - The user who performed the deletion
 * @param deletedTrackIds - Array of track IDs that were deleted
 * @param deletedPlaylistIds - Array of playlist IDs that were deleted
 * @param replacedPlaylistIds - Array of playlist IDs that were replaced/reverted
 * @returns Thunk that shows appropriate deletion notifications
 */
const showRemoteDeleteNotification = (
  userId: UserId | undefined,
  deletedTrackIds: number[],
  deletedPlaylistIds: number[],
  replacedPlaylistIds: number[],
): any => {
  return (_dispatch, getState) => {
    const state = getState()
    const visibleTracks = [
      ...getExplorerTrackIds(state),
      ...getShortlistTrackIds(state),
    ]
    const businessUser =
      userId !== undefined
        ? (
            Object.values(state.entities.businessUsers.users) as BusinessUser[]
          ).find(bu => bu.user.id === userId)
        : undefined
    const author = businessUser
      ? getUserDisplayName(businessUser.user)
      : 'another user'
    showRemoteNotification(
      deletedTrackIds,
      visibleTracks,
      id => {
        const track = getTrack(state, {
          id,
        })
        return track
          ? `${track.artist ?? ''} - ${track.name}`.replace(
              /(^\s*-\s+)|(\s*-\s+$)/,
              '',
            )
          : null
      },
      `The following tracks were just deleted by ${author}: `,
    )
    const visiblePlaylists = getExplorerPlaylistIds(state)
    showRemoteNotification(
      deletedPlaylistIds,
      visiblePlaylists,
      id => {
        const p = getPlaylist(state, {
          id,
        })
        return p ? p.name : null
      },
      `The following playlists were just deleted by ${author}: `,
    )
    showRemoteNotification(
      replacedPlaylistIds,
      visiblePlaylists,
      id => {
        const p = getPlaylist(state, {
          id,
        })
        return p ? p.name : null
      },
      `The following playlists were just reverted to a previous version by ${author}: `,
    )
  }
}

/**
 * Receives and processes deletion notifications for tracks and playlists.
 * Handles remote deletions by other users and shows appropriate notifications.
 * @param notification - The deletion notification containing deleted items
 * @returns Thunk that processes deletions and updates UI accordingly
 */
export const receiveDeleteNotification = (
  notification: JsonNotification | Record<string, any>,
): ((dispatch: any, getState: GetState) => Promise<void>) => {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    if (notification.type !== 'deletion') return
    const deletion = notification as Extract<
      JsonNotification,
      { type: 'deletion' }
    >
    const state = getState()
    // safe way to retrieve pathname if it exists(it's not in mobile app)
    const pathname = state.routing?.locationBeforeTransitions?.pathname ?? ''

    const { loaded, loading } = state.entities.businessUsers
    if (!loaded && !loading) {
      try {
        await dispatch(loadBusinessUsers())
      } catch {
        // name lookup is cosmetic; don't let a failed load block deletion side-effects
      }
    }

    const { deleted_track_ids } = deletion
    const deletedPlaylistIds = getAllDeletedPlaylists(deletion)
    const replacedPlaylists = getReplacedPlaylists(deletion)

    dispatch(
      showRemoteDeleteNotification(
        deletion.user?.id,
        deleted_track_ids || [],
        deletedPlaylistIds,
        replacedPlaylists.map(p => p.playlist_id),
      ),
    )

    if (deleted_track_ids && deleted_track_ids.length > 0) {
      remoteTrackDeleteAction(dispatch, deleted_track_ids, pathname)
    }

    if (deletedPlaylistIds.length > 0) {
      remotePlaylistDeleteAction(dispatch, deletedPlaylistIds, pathname)
    }

    if (replacedPlaylists.length > 0) {
      dispatch({
        type: PLAYLIST_VERSION_REPLACE_SUCCESS,
        replacedPlaylists,
      })
    }
  }
}
/**
 * Toggles the visibility of the notifications pane.
 * @returns Thunk that toggles notification pane visibility and tracks analytics
 */
export function toggleNotificationsPane(): (
  dispatch: any,
  getState: GetState,
) => void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const { isNotificationPaneVisible } = getState().notifications
    const meta = !isNotificationPaneVisible
      ? {
          mixpanel: {
            event: 'Open notification centre',
          },
        }
      : undefined
    dispatch({
      type: NOTIFICATION_PANE_TOGGLE,
      isNotificationPaneVisible: !isNotificationPaneVisible,
      meta,
    })
  }
}
/**
 * Opens the notification interface (opens sidebar).
 * @returns Action object to open the sidebar
 */
export function openNotification(): {
  type: string
} {
  return {
    type: SIDEBAR_OPEN,
  }
}
/**
 * Expands the notifications pane to show more content.
 * @returns Action object to expand notifications pane
 */
export function expandNotificationsPane(): {
  type: string
} {
  return {
    type: NOTIFICATION_PANE_EXPAND,
  }
}
/**
 * Dismisses a single notification both locally and on the server.
 * @param notification - The notification to dismiss
 * @returns Thunk that dismisses the notification and handles errors
 */
export function dismissNotification(
  notification: DismissibleNotification,
): (dispatch: any) => void {
  return (dispatch: (...args: Array<any>) => any) => {
    api
      .dismissNotification(notification)
      .catch(error => dispatch(sendGenericError(error)))
    dispatch({
      type: NOTIFICATION_PANE_DISMISS,
      notification,
    })
  }
}
/**
 * Dismisses all notifications both locally and on the server.
 * @returns Thunk that clears all notifications and handles errors
 */
export function dismissAllNotifications(): (dispatch: any) => void {
  return (dispatch: (...args: Array<any>) => any) => {
    api
      .dismissAllNotifications()
      .catch(error => dispatch(sendGenericError(error)))
    dispatch({
      type: PLAYLIST_NOTIFICATION_RESET,
    })
  }
}
/**
 * Processes track update notifications and refreshes track data.
 * Handles session-bound URLs by fetching fresh track data from server.
 * @param notifications - Array of track update notifications
 * @returns Thunk that updates track data in the store
 */
export const trackUpdateNotification =
  (
    notifications: Record<string, any>[],
  ): ((dispatch: any, getState: GetState) => Promise<any>) =>
  async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const isSessionBoundsURLs = isFeatureEnabled(getState(), {
      featureName: SESSION_BOUND_URLS,
    })
    const tracks = notifications.map(n => n.track_data)

    // if session bound urls are enabled,
    // fetch track from server as info from websocket message contains invalid playback urls
    // https://disco-team.atlassian.net/browse/DISCO-3915
    // missed notifications are supposed to contain valid playback urls so pass them as is
    if (isSessionBoundsURLs && tracks.length === 1) {
      const trackId = tracks[0].id
      const fetchedTrack = await api.getTrack(trackId)
      return dispatch(loadBatch([fetchedTrack]))
    }

    return dispatch(loadBatch(tracks))
  }
/**
 * Receives an important application update notification.
 * @returns Action object indicating an important update is available
 */
export function receiveImportantUpdateNotification(): {
  type: string
} {
  return {
    type: IMPORTANT_UPDATE_AVAILABLE,
  }
}
/**
 * Processes a shortlist snapshot from the server and merges with existing data.
 * Filters out duplicates and maintains local editing state.
 * @param data - Array of shortlists from server snapshot
 * @returns Thunk that initializes shortlists with merged data
 */
export const processShortlistSnapshot =
  (data: Shortlist[]): ((dispatch: any, getState: GetState) => any) =>
  (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    // drop empty shortlists from the new state
    const existingShortlists = getShortlists(getState()).filter(
      s => s.items.length !== 0 || (s.tempItems ?? []).length !== 0,
    )
    // exclude any opened for editing playlists if they included in the snapshot from the server
    const existingShortlistsWithoutDataPlaylists = existingShortlists.filter(
      s => !s.playlist_id || !data.find(d => d.playlist_id === s.playlist_id),
    )
    // exclude shortlists with the same ids if they are included in the snapshot
    const filteredExisitingShortlists =
      existingShortlistsWithoutDataPlaylists.filter(
        s => !data.find(d => d.id === s.id),
      )
    return dispatch(initShortlists([...filteredExisitingShortlists, ...data]))
  }
/**
 * Shows a success notification for subscription changes.
 * @returns Thunk that displays subscription change success notification
 */
export const successSubscriptionChangeNotification =
  (): ((dispatch: any) => any) => (_dispatch: (...args: Array<any>) => any) => {
    pushUINotification({
      level: 'success',
      title: 'Subscription updated!',
      message: `You have changed plans successfully`,
      action: {
        label: 'Go to settings',
        callback: () => NavigationService.push('/settings/billing'),
      },
      dismissible: 'button',
    })
  }
type NotificationProps = {
  location?: Location
  notification: JsonNotification | PongMessage
}
/**
 * Receives and processes lyric transcription completion notifications.
 * @param notification - The lyric transcription completion notification
 * @returns Thunk that handles lyrics completion processing
 */
export const receiveLyricTranscriptionCompleteNotification =
  (notification: JsonNotification): ((dispatch: any) => any) =>
  (dispatch: (...args: Array<any>) => any) => {
    if (notification.type !== 'lyric_transcription_completed') return
    dispatch(
      handleLyricsComplete(
        notification.track_id,
        notification.track_name,
        notification.lyrics,
        notification.metadata,
      ),
    )
  }
/**
 * Processes incoming websocket notifications and routes them to appropriate handlers.
 * Handles various notification types including playlist updates, deletions, subscriptions, etc.
 * @param params - Object containing location and notification data
 * @param params.location - Current location for routing context
 * @param params.notification - The websocket notification to process
 * @returns Thunk that routes notification to the appropriate handler
 */
export const processWebsocketNotification =
  ({
    location,
    notification: anyNotification,
  }: NotificationProps): ((
    dispatch: (...args: Array<any>) => any,
    getState: GetState,
  ) => void) =>
  (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    if ('pong' in anyNotification) return
    if (!anyNotification.type)
      return processUnknownNotification(anyNotification)
    const notification = anyNotification as JsonNotification

    switch (notification.type) {
      case 'auth':
        break

      case 'access_share':
      case 'group_receive_share':
      case 'receive_share':
      case 'send_upload':
      case 'group_receive_upload':
      case 'bulk_upload_complete':
      case 'add_to_disco_complete':
      case 'welcome_to_disco':
      case 'aggregated':
      case 'receive_upload':
        dispatch(receivePlaylistNotification(notification))
        break
      case 'receive_brief_submission':
        dispatch(receiveBriefSubmissionNotification(notification))
        break
      case 'discussions_receive_mention':
        dispatch(receiveMentionNotification(notification))
        break
      case 'discussions_receive_reaction':
        // Gated behind the same flag as mentions until reactions ship broadly.
        if (getConstanceSetting(getState(), NOTES_V2_MENTIONS)) {
          dispatch(receiveReactionNotification(notification))
        }
        break

      case 'receive_share_password':
        // for inter-DISCO shares the password does not apply
        // https://app.asana.com/0/35126173840535/1178888351745738/f
        break

      case 'track_update':
        dispatch(trackUpdateNotification([notification]))
        break

      case 'important_update':
        if (notification.target === 'v1') {
          if (FRONTEND_VERSION === 'v2') return
        }

        dispatch(receiveImportantUpdateNotification())
        break

      case 'instant_instrumental_created': // also used for a cappella completions
        dispatch(receiveAudioSeparationNotification(notification))
        break

      case 'channel_share_invite':
        dispatch(receiveChannelShareNotification(notification))
        break

      case 'channel_share_invite_accepted':
        dispatch(acceptedChannelShareNotification(notification))
        break

      case 'lyric_transcription_completed':
        dispatch(receiveLyricTranscriptionCompleteNotification(notification))
        break

      case 'deletion':
        // receiveDeleteNotification reads state.explorer / state.shortlists / state.tracks
        // / state.playlists and pushes web routes. None of which exist on mobile.
        // Mobile handles deletions in components/NotificationManager.tsx instead
        if (__REACT_NATIVE__) break

        if (notification.client_id !== browserInstanceId) {
          dispatch(receiveDeleteNotification(notification))
        }

        break

      case 'signup_success_event_update':
        dispatch(
          updateUserBusiness(notification.business_data, {
            message: 'Plan updated!',
            level: 'success',
          }),
        )
        break

      case 'similarity_search_results': {
        const trackIds = notification.tracks
        const request = api.similarTracksRequests[notification.request_id]

        if (!request) {
          console.log('no inflight request for notification', notification)
          return
        }

        if (!notification.success) {
          return request.reject(
            new Error(
              notification.error ||
                notification.message ||
                'Similarity search failed',
            ),
          )
        }

        api
          .getExistingObjects(null, null, trackIds)
          .then(serverState => {
            const tracks = trackIds.map(t => serverState.tracks[t])
            request.resolve({
              results: tracks,
              title: notification.title,
            })
          })
          .catch(err => {
            request.reject(
              err || new Error('Similarity search could not load tracks'),
            )
          })
        break
      }

      // TODO: unify similarity_search_results_aggregated and similarity_search_results
      case 'similarity_search_results_aggregated': {
        const trackIds = notification.tracks
        // localStorage.getItem('simsearch_debug') === '1'
        //   ? similaritySearchResultsAggregatedTracks
        //   : notification.tracks
        const request =
          api.aggregatedSimilarTracksRequests[notification.request_id]

        if (!request) {
          console.log('no inflight request for notification', notification)
          return
        }

        if (!notification.success) {
          return request.reject(
            new Error(
              notification.error ||
                notification.message ||
                'Aggregated similarity search failed',
            ),
          )
        }

        api
          .getAggregatedSearchBatchTracks(trackIds)
          .then(response => {
            request.resolve(response)
          })
          .catch(err => {
            request.reject(
              err ||
                new Error('Aggregated similarity search could not load tracks'),
            )
          })
        break
      }

      case 'client_library_access_requested':
      case 'client_library_access_granted':
        dispatch(receiveLibraryNotification(notification))
        break

      case 'shortlist_snapshot':
        if (notification.data)
          dispatch(processShortlistSnapshot(notification.data))
        break

      case 'subscription_resume':
        dispatch(loadBusinessData())

        if (location?.pathname === '/expired-subscription') {
          NavigationService.push('/')
        }

        break

      case 'subscription_product_change_event_update':
        dispatch(loadBusinessData())
        dispatch(successSubscriptionChangeNotification())
        break

      case 'group_create':
        console.log('unhandled', notification)
        break

      // TODO: add redux handler
      case 'group_delete':
        console.log('unhandled', notification)
        break

      // TODO: add redux handler
      case 'group_update':
        console.log('unhandled', notification)
        break

      // TODO: add redux handler
      case 'watermark_leak_trace_update':
        dispatch(receiveLeakTracingNotification(notification))
        break

      case 'playlist_update': {
        const playlistId = notification.data?.id

        if (playlistId) {
          dispatch(
            applyPartialPlaylistUpdate([playlistId], {
              watermarking_status: notification.data?.watermarking_status,
              watermark: notification.data?.watermark,
            }),
          )
        }

        break
      }
      case 'business_update':
        if (notification.data?.watermarked_playlists_quota_used) {
          const { watermarked_playlists_quota_used } = notification.data
          dispatch(
            updateBusinessPrivateMetadata({
              watermarked_playlists_quota_used,
            }),
          )
        }

        if (
          typeof notification.data?.lyric_transcription_quota_left !==
          'undefined'
        ) {
          const { lyric_transcription_quota_left } = notification.data
          dispatch(
            updateBusinessPrivateMetadata({
              lyric_transcription_quota_left,
            }),
          )
        }

        break

      case 'external_upload_progress':
        dispatch(
          updateTempItemProgress(notification.shortlist_id, {
            tempId: notification.external_id,
            progress: notification.progress,
            status: notification.progress === 100 ? 'processing' : undefined,
            externalUploadId: notification.external_upload_id,
          }),
        )
        break

      case 'external_upload_finished':
        dispatch(
          processExternalUploadFinish(notification.shortlist_id, {
            tempId: notification.external_id,
            track: { ...notification.track, ...notification.external_upload },
          }),
        )
        break

      case 'external_upload_error':
        dispatch(
          // @ts-expect-error - todo fix later
          removeTempItem(notification.shortlist_id, notification.external_id),
        )
        break

      case 'logout_all':
        NavigationService.push('/logout')
        break

      default:
        processUnknownNotification(notification)
        break
    }
  }
